import { Monitor } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { DecarbContentLayout } from '../components/DecarbContentLayout';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import {
  EnergyKind,
  MonitorWithMetrics,
  TechSortDimensions,
  TechSortState,
  getEnergyField,
  sortTechItemsByState,
} from '../types';
import { MonitoringUnit } from './MonitoringUnit';

type MonitoringSelectionDetailsProps = {
  helperTextComponent: React.ReactNode;
  availableMonitors: MonitorWithMetrics[];
  selectedMonitors: (MonitorWithMetrics & { count: number })[];
  costEnergyAndCoverageMetrics?: {
    // undefined: no stored figure to show yet (renders the placeholder)
    cost?: number;
    energyDemand: number;
    systemCoveragePercentage: number;
  };
  isLoading?: boolean;
  onAdd: (item: MonitorWithMetrics) => void;
  onRemove: (item: MonitorWithMetrics & { count: number }) => void;
  onChangeCount: (
    item: MonitorWithMetrics & { count: number },
    newCount: number,
  ) => void;
  readOnly?: boolean;
};

export const MonitoringSelectionDetails: React.FC<
  MonitoringSelectionDetailsProps
> = ({
  helperTextComponent,
  availableMonitors,
  selectedMonitors,
  costEnergyAndCoverageMetrics,
  isLoading,
  onAdd,
  onRemove,
  onChangeCount,
  readOnly,
}) => {
  const { cost, energyDemand } = costEnergyAndCoverageMetrics || {};

  const [searchString, setSearchString] = useState('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(
      availableMonitors.map((item) => getEnergyField(item)).filter(Boolean),
    );
    return [...types] as EnergyKind[];
  }, [availableMonitors]);

  const filteredAndSortedItems = useMemo(() => {
    let items = availableMonitors;

    // Apply search filter
    if (searchString.length > 0) {
      items = items.filter((item) =>
        item.name.toLowerCase().includes(searchString.toLowerCase()),
      );
    }

    // Apply energy source filter (include only selected energy types)
    if (filter.length > 0) {
      items = items.filter((item) => {
        const energy = getEnergyField(item);
        return energy ? filter.includes(energy as EnergyKind) : true;
      });
    }

    // Apply sort
    items = sortTechItemsByState(items, sortState);

    return items;
  }, [availableMonitors, searchString, filter, sortState]);

  return (
    <DecarbContentLayout
      availableItems={
        readOnly
          ? []
          : filteredAndSortedItems.map((item) => (
              <MonitoringUnit
                key={item.id}
                monitor={item}
                selected={false}
                actions={{ onAdd: () => onAdd(item) }}
              />
            ))
      }
      selectedItems={selectedMonitors.map((item) => (
        <MonitoringUnit
          key={item.id}
          count={item.count}
          monitor={item}
          selected={true}
          isLoading={isLoading}
          readOnly={readOnly}
          actions={{
            onRemove: () => onRemove(item),
            onIncrement: () => onChangeCount(item, item.count + 1),
            onDecrement: () => onChangeCount(item, item.count - 1),
            onChangeCount: (newCount: number) => onChangeCount(item, newCount),
          }}
        />
      ))}
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'Monitor'}
          systemCoverageIcon={<Monitor />}
          systemCoverageHelperText={
            'Choose enough monitors to get 100% coverage'
          }
          energyDemand={energyDemand}
          capitalCost={cost}
          coveragePercentage={
            costEnergyAndCoverageMetrics?.systemCoveragePercentage
          }
        />
      }
      helperText={helperTextComponent}
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) =>
                prev.includes(option)
                  ? prev.filter((val) => val !== option)
                  : [...prev, option],
              )
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
    />
  );
};
